import React from 'react'
import clsx from 'clsx'
import Chart from 'react-apexcharts'
import { FormattedMessage, useIntl } from 'react-intl'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { channelsFormatMessage } from '../constants'
import { getLocale } from 'app/translations/intl-context'
import { getLocalizedDate } from 'commons/date'

import moment from 'moment'

const useStyles = makeStyles((theme) => ({
  customLineChart: {
    fontFamily: 'Roboto',
    color: '#404040',
    '& .apexcharts-tooltip': {
      background: 'rgba(255, 255, 255, 0.89)',
      boxShadow: 'none',
    },
    '& .apexcharts-pan-icon.apexcharts-selected svg path': {
      stroke: '#008ffb',
      fill: '#008ffb',
    },
    '& .tooltip': {
      width: '250px',
      padding: '10px',
      borderRadius: '8px',
    },
    '& .tooltipTitle': {
      fontSize: '12px',
      fontWeight: 500,
    },
    '& .tooltipContent': {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '12px',
      margin: '3px 0',
    },
  },
  totalUsage: {
    backgroundColor: 'rgba(0, 115, 177, 0.06)',
    color: '#404040',
  },
  color: {
    color: '#404040',
  },
  textBalance: {
    fontSize: '20px',
    color: 'rgba(0, 178, 173, 1)',
  },
}))

const panIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="#000000" xmlns="http://www.w3.org/2000/svg">
<path d="M1.33214 15.163L8.59605 22.7353L1.33214 15.163ZM1.33214 15.163L3.36313 13.3675L3.36319 13.3674C3.96765 12.8312 4.85503 12.724 5.56645 13.1041L5.56656 13.1042L7.97656 14.3842L8.05001 14.4232V14.34V4.78999C8.05001 3.43761 9.14762 2.33999 10.5 2.33999C10.6662 2.33999 10.8331 2.35955 11.0013 2.38923L11.0561 2.39889L11.0599 2.34345C11.1481 1.06927 12.2067 0.0599945 13.5 0.0599945C14.3428 0.0599945 15.0776 0.481214 15.5187 1.12816L15.5416 1.16173L15.5791 1.1462C15.8626 1.02891 16.176 0.969995 16.5 0.969995C17.8524 0.969995 18.95 2.06761 18.95 3.41999V3.69999V3.76024L19.0092 3.74914C19.167 3.71956 19.3338 3.69999 19.5 3.69999C20.8524 3.69999 21.95 4.79761 21.95 6.14999V20C21.95 22.1824 20.1824 23.95 18 23.95H11.45C10.3836 23.95 9.33665 23.5055 8.59609 22.7354L1.33214 15.163ZM4.10693 15.2425L4.06781 15.277L4.10395 15.3146L9.96395 21.4146L9.9642 21.4149C10.3531 21.814 10.8858 22.05 11.44 22.05H18C19.1276 22.05 20.05 21.1276 20.05 20V6.14999C20.05 5.84238 19.8076 5.59999 19.5 5.59999C19.1924 5.59999 18.95 5.84238 18.95 6.14999V11.95H17.05V3.41999C17.05 3.11238 16.8076 2.86999 16.5 2.86999C16.1924 2.86999 15.95 3.11238 15.95 3.41999V11.95H14.05V2.50999C14.05 2.20238 13.8076 1.95999 13.5 1.95999C13.1924 1.95999 12.95 2.20238 12.95 2.50999V11.95H11.05V4.78999C11.05 4.48238 10.8076 4.23999 10.5 4.23999C10.1917 4.23999 9.95001 4.49303 9.95001 4.78999V17.577L4.67339 14.7858L4.64284 14.7696L4.61693 14.7925L4.10693 15.2425Z" fill="#6E8192" stroke="#6E8192" stroke-width="0.1"/>
</svg>`

export function LineChart({ lineData }) {
  const { formatMessage } = useIntl()
  const classes = useStyles()

  const locale = getLocale()

  const chart = {
    series: [
      {
        data: lineData.y.map((data) => data.total),
      },
    ],
    options: {
      chart: {
        id: 'line-chart-usage-summary',
        type: 'area',
        height: 450,
        zoom: {
          autoScaleYaxis: false,
          enabled: true,
        },
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: panIcon,
            reset: true,
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: lineData.y.length === 1 ? 4 : 0,
        colors: 'rgba(0, 143, 191, 1)',
        strokeColors: 'rgba(0, 143, 191, 1)',
        hover: {
          size: 4,
        },
      },
      grid: {
        show: true,
        borderColor: 'rgba(217, 217, 217, 0.3)',
        height: 0.2,
        position: 'back',
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      noData: {
        text: 'Loading....',
        style: {
          fontSize: '16px',
          colors: '#000000',
          fontFamily: 'Roboto',
        },
      },
      xaxis: {
        type: 'datetime',
        categories: lineData.x.map((data) => data),
        labels: {
          style: {
            colors: '#404040',
            fontFamily: 'Roboto',
          },
          datetimeUTC: false,
          formatter: (val) =>
            `${new Date(val).getDate()} ${moment(val).format('MMM')}`,
        },
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: true,
          color: '#DADADA',
          height: 1,
          width: '100%',
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        forceNiceScale: true,
        axisBorder: {
          show: true,
          color: '#DADADA',
        },
        // min: min => {
        //   return min;
        // },
        // max: max => {
        //   return max;
        // },
        tickAmount: 6,
        labels: {
          show: true,
          style: {
            colors: '#404040',
            fontFamily: 'Roboto',
          },
          formatter: (value) => {
            return Number(value ?? 0).toLocaleString('en-US')
          },
        },
      },
      tooltip: {
        marker: {
          show: false,
        },
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const dataTooltip = lineData.y[dataPointIndex]
          return (
            '<div class="tooltip">' +
            '<p class="tooltipTitle">' +
            getLocalizedDate(dataTooltip.date, 'DATE') +
            '</p>' +
            dataTooltip.data
              .map((item) => {
                return (
                  '<div class="tooltipContent">' +
                  '<p>' +
                  formatMessage({
                    id: `channel.${channelsFormatMessage[item.type]}`,
                  }) +
                  '</p>' +
                  '<p>' +
                  Number(item.credits ?? 0).toLocaleString('en-US') +
                  '</p>' +
                  '</div>'
                )
              })
              .join('') +
            '</div>'
          )
        },
      },
      colors: ['rgba(0, 178, 173, 1)'],
      fill: {
        type: 'gradient',
        colors: ['rgba(0, 178, 173, 1)', 'rgba(217, 217, 217, 0)'],
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 80],
        },
      },
    },
  }

  return (
    <div>
      <div className="bg-white pb-20 px-10 rounded-8 border-1">
        <div className="w-full flex justify-between my-10">
          <div
            className={clsx(
              'text-16 font-bold flex items-center',
              classes.color,
            )}
          >
            <FormattedMessage id="text.activity" />
          </div>
          <div
            className={clsx(
              'flex rounded-lg text-14 px-5 py-3 items-center',
              classes.totalUsage,
            )}
          >
            <FormattedMessage id="text.total.usage" />
            <Typography
              className={clsx('text-20 font-bold ml-10', classes.textBalance)}
            >
              {Number(lineData.total ?? 0).toLocaleString('en-US')}
            </Typography>
          </div>
        </div>
        <div className={classes.customLineChart}>
          <Chart
            options={chart.options}
            series={chart.series}
            type="area"
            height={450}
          />
        </div>
      </div>
    </div>
  )
}
